import React, { useState, useEffect } from "react"
import { ManageEstateData } from "../estate/"
import { ExtractId } from "../../../util"
import Paginator from "../../Paginator"
import { getManageDeviceList } from "../../../api/manage_device_list"
import { getManageEstateList } from "../../../api/manage_estate_list"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"

import ManageDeviceCreate from "./create"
import ManageDeviceUpdate from "./update"
import ManageDeviceDelete from "./delete"
import ManageDeviceBulkCreate from "./bulk_create"

import DashboardLayout from "../../components/DashboardLayout"
import DashboardNavbar from "../../components/DashboardNavbar"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Button from "@mui/material/Button"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

export interface ManageDeviceData {
  id: string
  catv_id: string
  estate_id: string | null
  estate_name: string | null
  facility_id: string | null
  medical_record_id: string | null
  serial_no: string
  create_date: number
  update_date: number
  is_delete: number
}

const ManageDeviceTileList: React.FC = () => {
  const { startLoading, stopLoading } = useLoading()
  const [errorMessage, setErrorMessage] = useState("")
  const { showConnectionError } = useConnectionError()
  const navigate = useNavigate()

  const [searchText, setSearchText] = useState("")
  const [deviceData, setDeviceData] = useState<ManageDeviceData[]>([])
  const [filteredData, setFilteredData] = useState<ManageDeviceData[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  const [deviceCreate, setDeviceCreate] = useState<boolean>(false)
  const [deviceEdit, setDeviceEdit] = useState<boolean>(false) // 編集モーダルの表示/非表示
  const [deviceDelete, setDeviceDelete] = useState<boolean>(false) // 編集モーダルの表示/非表示
  const [selectedDevice, setSelectedDevice] = useState<ManageDeviceData | null>(null) // 編集対象のサービス提供元データ
  const [deviceBulkCreate, setDeviceBulkCreate] = useState<boolean>(false)

  // 開発者アカウントでの閲覧を可能とする
  const [isViewMode, setIsViewMode] = useState<boolean>(false)

  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  const fetchManageDeviceList = async () => {
    try {
      startLoading()
      const manageDeviceData = await getManageDeviceList()
      const manageEstateData = await getManageEstateList()

      // estate_id をキーとしたマップを作成（型を明示）
      const estateMap: Record<string, string> = manageEstateData.estate_list.reduce(
        (map: Record<string, string>, estate: ManageEstateData) => {
          map[estate.id.split("|")[1]] = estate.name
          return map
        },
        {}
      )

      // 開発者アカウントでの閲覧を可能とする
      const filteredDeviceData = manageDeviceData.device_list

      // device_list に estate_name を付与
      const enrichedDeviceData = filteredDeviceData.map((device: ManageDeviceData) => {
        const estateName = device.estate_id ? estateMap[device.estate_id] : undefined
        return {
          ...device,
          estate_name: estateName || "未登録" // estate_id が無い場合や該当が無い場合
        }
      })

      setDeviceData(enrichedDeviceData)

      const auth = localStorage.getItem("account_type")
      if (auth == "service") {
        let isViewModeWork = true
        const view_mode = localStorage.getItem("view_mode")
        if (view_mode == "2") {
          const estate_id = localStorage.getItem("show_mode_estate")
          if (estate_id === "" || estate_id === "-") {
            isViewModeWork = false
          }
        }
        setIsViewMode(isViewModeWork)
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else if (!error.response) {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  useEffect(() => {
    fetchManageDeviceList()
  }, [])

  // 検索処理
  const handleSearch = () => {
    const filtered = deviceData.filter((device) => {
      const matchesText = searchText === "" || device.serial_no?.includes(searchText)

      return matchesText
    })
    setFilteredData(filtered)
    setCurrentPage(1)
  }

  // 初期表示時に初期ページを表示
  useEffect(() => {
    setFilteredData(deviceData)
  }, [deviceData])

  const handleCreate = () => {
    //console.log("追加ボタンがクリックされた ");
    setDeviceCreate(true)
  }

  const handleEdit = (id: string) => {
    //console.log("編集ボタンがクリックされた " + id);
    const deviceToEdit = deviceData.find((device) => device.id === id)
    if (deviceToEdit) {
      setSelectedDevice(deviceToEdit) // 編集対象をセット
      setDeviceEdit(true) // 編集モーダルを表示
    }
  }

  const handleDelete = (id: string) => {
    //console.log("削除ボタンがクリックされた " + id);
    const deviceToEdit = deviceData.find((device) => device.id === id)
    if (deviceToEdit) {
      setSelectedDevice(deviceToEdit) // 編集対象をセット
      setDeviceDelete(true) // 編集モーダルを表示
    }
  }

  const handleClose = async (num: number) => {
    if (num === 1) {
      // APIを再取得
      await fetchManageDeviceList()

      // データ更新後に検索を行う
      setDeviceCreate(false)
      setDeviceEdit(false)
      setDeviceDelete(false)
      setDeviceBulkCreate(false)

      // フィルタリングを再実行して、画面を更新
      handleSearch()
    } else {
      setDeviceCreate(false)
      setDeviceEdit(false)
      setDeviceDelete(false)
      setDeviceBulkCreate(false)
    }
  }

  const totalPages = Math.ceil(filteredData.length / itemsPerPage)

  const validate = (message: string) => {
    setSearchText(message)

    if (message.length > 12) {
      setErrorMessage("入力文字数は12文字以内です")
    } else {
      setErrorMessage("") // エラーがない場合はクリア
    }
  }

  return (
    <DashboardLayout>
      {deviceCreate && <ManageDeviceCreate onClose={handleClose} />}
      {deviceBulkCreate && <ManageDeviceBulkCreate onClose={handleClose} />}
      {deviceEdit && selectedDevice && (
        <ManageDeviceUpdate
          onClose={handleClose}
          deviceData={selectedDevice} // 編集するサービス提供元データを渡す
        />
      )}
      {deviceDelete && selectedDevice && (
        <ManageDeviceDelete
          onClose={handleClose}
          deviceData={selectedDevice} // 削除するサービス提供元データを渡す
        />
      )}
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3} pb={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            デバイス一覧
          </MDTypography>
        </MDBox>
      </MDBox>
      {/* 検索フォームとボタンエリア */}
      <MDBox
        mb={2}
        display="flex"
        justifyContent="space-between" // 左右端に配置
        alignItems="center"
        flexWrap="wrap"
        sx={{ fontSize: "0.875rem", color: "text.primary" }}
      >
        {/* 左側：検索エリア */}
        <MDBox display="flex" alignItems="center" gap={1}>
          <div style={{ position: "relative", display: "inline-block" }}>
            <MDInput
              label="シリアルNoで検索"
              value={searchText}
              onChange={(e) => validate(e.target.value)}
              sx={{ width: "240px", backgroundColor: "#FFFFFF", borderRadius: "10px" }}
              error={Boolean(errorMessage)}
            />
            {errorMessage && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "0",
                  fontSize: "12px",
                  color: "red",
                  marginTop: "4px",
                  whiteSpace: "nowrap"
                }}
              >
                {errorMessage}
              </div>
            )}
          </div>
          <MDButton variant="gradient" color="info" size="medium" onClick={handleSearch} disabled={errorMessage !== ""}>
            <Icon>search</Icon>&nbsp; 検索
          </MDButton>
        </MDBox>

        {/* 中央：可変スペーサー */}
        <MDBox sx={{ flexGrow: 1 }} />

        {/* 右側：アクションボタンエリア */}
        {!isViewMode && (
          <MDBox display="flex" gap={1}>
            <MDButton variant="gradient" color="info" size="medium" onClick={() => setDeviceBulkCreate(true)}>
              一括追加
            </MDButton>
            <MDButton variant="gradient" color="info" size="medium" onClick={handleCreate}>
              追加
            </MDButton>
          </MDBox>
        )}
      </MDBox>
      {/* デバイス一覧 */}
      <MDBox mt={4}>
        <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "calc(100vh - 300px)" }}>
          <Table stickyHeader>
            <TableHead sx={{ position: "sticky", display: "table-header-group", zIndex: 3 }}>
              <TableRow
                sx={{
                  "& .MuiTableCell-root": {
                    fontSize: "0.875rem",
                    color: "text.primary",
                    fontWeight: "bold",
                    backgroundColor: "#FFFFFF"
                  }
                }}
              >
                <TableCell align="left">デバイスID</TableCell>
                <TableCell align="left">シリアルNo</TableCell>
                <TableCell align="left">提供対象管理業者ID</TableCell>
                <TableCell align="left">提供対象管理業者名</TableCell>
                {!isViewMode && (
                  <>
                    <TableCell align="center" style={{ minWidth: "75px" }}>
                      編集
                    </TableCell>
                    <TableCell align="center" style={{ minWidth: "75px" }}>
                      削除
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& .MuiTableCell-root": {
                      fontSize: "0.875rem",
                      color: "text.primary",
                      fontWeight: "normal",
                      zIndex: 0
                    }
                  }}
                >
                  <TableCell align="left">
                    <div
                      style={{
                        minWidth: "50px",
                        maxWidth: "300px",
                        width: "10vw",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={ExtractId(row.id) || undefined}
                    >
                      {ExtractId(row.id)}
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "100px" }}>{row.serial_no}</div>
                  </TableCell>
                  <TableCell align="left">
                    <div
                      style={{
                        minWidth: "50px",
                        maxWidth: "300px",
                        width: "10vw",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                      title={row.estate_id || undefined}
                    >
                      {row.estate_id ? row.estate_id : ""}
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div style={{ minWidth: "200px" }}>{row.estate_id ? row.estate_name : ""}</div>
                  </TableCell>
                  {!isViewMode && (
                    <>
                      <TableCell align="right">
                        {(row.medical_record_id == null || row.medical_record_id == "") && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ color: "#FFF" }}
                            onClick={() => {
                              handleEdit(`${row.id}`)
                            }}
                          >
                            編集
                          </Button>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(row.medical_record_id == null || row.medical_record_id == "") && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={{ color: "#FFF" }}
                            onClick={() => {
                              handleDelete(`${row.id}`)
                            }}
                          >
                            削除
                          </Button>
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
      {/* ページャー */}
      <Paginator
        key={"ManageDeviceTileListPaginator"}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </DashboardLayout>
  )
}

export default ManageDeviceTileList
